// Header envelope with the unread badge, login toast and the chat window.
// Lives in the layout header for authenticated users only.

import { useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { useFirebase } from './FirebaseContextProvider';
import { useModal } from './ModalProvider';
import Tooltip from './Tooltip';
import ChatsModalBody from './ChatsModalBody';
import './Chats.scss';

// The chats unread at the last look, so only a newly unread one is announced.
// Module-level so it survives component remounts caused by route changes.
let announcedIds = null;

export default function Chats() {
  const { user, chats, unreadCount, isChatsSynced, viewedChatRef } =
    useFirebase();
  const { openModal } = useModal();

  const openChatsRef = useRef(null);

  // Which chat to open is the body's own call — it knows what is unread
  const openChats = () => {
    openModal({
      header: 'Chats',
      body: <ChatsModalBody />,
    });
  };

  openChatsRef.current = openChats;

  // Toasts a newly unread chat (all of them at login), bar the one on screen.
  // Synced: unread is read off a missing watermark, which a cache can fake
  useEffect(() => {
    if (!user) {
      announcedIds = null;
      return;
    }
    if (!isChatsSynced) return;

    const unreadIds = chats
      .filter((chat) => chat.hasUnread)
      .map((chat) => chat.id);
    // The viewed chat is remembered too: closed before its mark-read lands,
    // it is already known and does not toast
    const elsewhere = unreadIds.filter(
      (id) => !viewedChatRef.current.includes(id),
    );
    if (elsewhere.some((id) => !announcedIds?.has(id)))
      toast.info(
        `You have ${elsewhere.length} unread chat${elsewhere.length > 1 ? 's' : ''}`,
        {
          onClick: () => openChatsRef.current(),
        },
      );
    announcedIds = new Set(unreadIds);
  }, [user, isChatsSynced, chats, viewedChatRef]);

  if (!user) return null;

  const label = unreadCount
    ? `${unreadCount} unread chat${unreadCount > 1 ? 's' : ''}`
    : 'Chats';

  return (
    <Tooltip label={label}>
      <div
        className='chats'
        onClick={openChats}
        role='button'
        aria-label={label}
      >
        <span className='icon fa-envelope-o' />
        {unreadCount > 0 && <span className='chats-badge'>{unreadCount}</span>}
      </div>
    </Tooltip>
  );
}
